import React, { useState } from 'react';
import { safeString, safeUrl, formatCurrency } from '@/src/utils/helpers';
import { HeartIcon, ShareIcon, TagIcon, CalendarIcon, HomeIcon, InfoIcon, MapIcon, ChevronDownIcon } from '@/src/components/icons';
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from '@/src/components/ui/card';
import { Button } from '@/src/components/ui/button';
import { Badge } from '@/src/components/ui/badge';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/src/components/ui/collapsible';
import PropertyMap from './PropertyMap';
import PropertyNoteEditor from './PropertyNoteEditor';
import { useSalesHistory } from '@/src/hooks/useSalesHistory';

const PropertyDetail = ({ property, onBack, isFavorite, toggleFavorite, getPropertyTags, onOpenTagManager, getNote, saveNote, deleteNote }) => {
  const [showShareSuccess, setShowShareSuccess] = useState(false);
  const { sales, loading: salesLoading } = useSalesHistory(property?.roll_number);
  const [salesHistoryOpen, setSalesHistoryOpen] = useState(false);

  const shareProperty = async () => {
    if (!property) return;
    
    try {
      const address = safeString(property.full_address, 'Unknown Address');
      const currentUrl = window.location.href;
      
      const shareMessage = `Check out this property: ${address}
${formatCurrency(property.total_assessed_value)} • ${safeString(property.neighbourhood_area, 'Unknown Area')}
Built: ${safeString(property.year_built, 'N/A')} • ${safeString(property.total_living_area, 'N/A')} sq ft

View details: ${currentUrl}

Shared from Winnipeg Property Explorer`;

      await navigator.clipboard.writeText(shareMessage);
      
      setShowShareSuccess(true);
      setTimeout(() => setShowShareSuccess(false), 3000);
      
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      
      try {
        const textArea = document.createElement('textarea');
        const address = safeString(property.full_address, 'Unknown Address');
        const currentUrl = window.location.href;
        const shareMessage = `Check out this property: ${address}
${formatCurrency(property.total_assessed_value)} • ${safeString(property.neighbourhood_area, 'Unknown Area')}
Built: ${safeString(property.year_built, 'N/A')} • ${safeString(property.total_living_area, 'N/A')} sq ft

View details: ${currentUrl}

Shared from Winnipeg Property Explorer`;
        
        textArea.value = shareMessage;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        
        setShowShareSuccess(true);
        setTimeout(() => setShowShareSuccess(false), 3000);
      } catch (fallbackError) {
        console.error('Fallback copy failed:', fallbackError);
        alert('Unable to copy to clipboard. Please copy the URL manually.');
      }
    }
  };

  const getMonthName = (month) => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months[month - 1] || '';
  };

  if (!property) {
    return (
      <Card className="max-w-4xl mx-auto">
        <CardContent className="p-8 text-center">
          <p className="text-muted-foreground">Property not found</p>
          <Button onClick={onBack} className="mt-4">
            Back to Search
          </Button>
        </CardContent>
      </Card>
    );
  }

  const address = safeString(property.full_address, 'Unknown Address');
  const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address + ', Winnipeg, MB, Canada')}`;
  const addressSlug = safeUrl(property.full_address);
  const honestDoorUrl = addressSlug ? `https://www.honestdoor.com/property/${addressSlug}-winnipeg-mb` : null;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <Button onClick={onBack} variant="outline" className="flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                <polyline points="15,18 9,12 15,6"></polyline>
              </svg>
              Back to Search
            </Button>
            <div className="flex items-center gap-2">
              <button
                onClick={shareProperty}
                className="p-2 rounded-full transition-colors duration-200 hover:bg-gray-100 text-gray-600 hover:text-blue-600"
                title="Share property"
              >
                <ShareIcon className="w-6 h-6" />
              </button>
              <button
                onClick={() => toggleFavorite(property.roll_number)}
                className={`p-2 rounded-full transition-colors duration-200 hover:bg-gray-100 ${
                  isFavorite(property.roll_number) 
                    ? 'text-red-500 hover:text-red-600' 
                    : 'text-gray-400 hover:text-gray-600'
                }`}
                title={isFavorite(property.roll_number) ? 'Remove from favorites' : 'Add to favorites'}
              >
                <HeartIcon filled={isFavorite(property.roll_number)} className="w-6 h-6" />
              </button>
              <button
                onClick={() => onOpenTagManager(property)}
                className="p-2 rounded-full transition-colors duration-200 hover:bg-gray-100 text-gray-600 hover:text-blue-600"
                title="Manage tags"
              >
                <TagIcon className="w-6 h-6" />
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      {showShareSuccess && (
        <div className="fixed top-4 right-4 bg-green-500 text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 z-50 animate-in slide-in-from-top duration-300">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20,6 9,17 4,12"></polyline>
          </svg>
          Property link copied to clipboard!
        </div>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="text-3xl mb-2">{address}</CardTitle>
              <CardDescription className="text-lg">
                {safeString(property.neighbourhood_area, 'Unknown Area')}
              </CardDescription>
              <div className="mt-2 space-y-2">
                <div>
                  <span className="text-sm bg-stone-100 text-stone-800 px-3 py-1 rounded">
                    Roll #: {property.roll_number}
                  </span>
                </div>
                {getPropertyTags && getPropertyTags(property.roll_number)?.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {getPropertyTags(property.roll_number).map(tag => (
                      <Badge 
                        key={tag.id}
                        style={{ backgroundColor: tag.color, color: '#ffffff', border: 'none' }}
                      >
                        {tag.name}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg">
            <h3 className="text-lg font-semibold text-stone-900 mb-2">Assessment Value</h3>
            <p className="text-4xl font-bold text-indigo-600">
              {formatCurrency(property.total_assessed_value)}
            </p>
          </div>

          {!salesLoading && (
            <Collapsible open={salesHistoryOpen} onOpenChange={setSalesHistoryOpen}>
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-lg">
                <CollapsibleTrigger asChild>
                  <button className="w-full flex items-center justify-between text-left">
                    <h3 className="text-lg font-semibold text-stone-900 flex items-center">
                      <CalendarIcon />
                      <span className="ml-2">
                        Sales History{sales.length > 0 && ` (${sales.length})`}
                      </span>
                    </h3>
                    <ChevronDownIcon
                      className={`w-5 h-5 transition-transform ${salesHistoryOpen ? 'rotate-180' : ''}`}
                    />
                  </button>
                </CollapsibleTrigger>

                <CollapsibleContent>
                  {sales.length > 0 ? (
                    <div className="space-y-3 mt-4">
                      {sales.map((sale, index) => (
                        <div
                          key={index}
                          className="flex justify-between items-center p-3 bg-white rounded-md"
                        >
                          <div>
                            <p className="font-medium text-stone-900">
                              {getMonthName(sale.sale_month)} {sale.sale_year}
                            </p>
                            {sale.building_type && (
                              <p className="text-sm text-stone-600">{sale.building_type}</p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-emerald-700">
                              {formatCurrency(sale.sale_price)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-stone-500 text-sm mt-4">No sales history available</p>
                  )}
                </CollapsibleContent>
              </div>
            </Collapsible>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-stone-50 p-4 rounded-lg">
              <div className="flex items-center mb-2">
                <CalendarIcon />
                <span className="ml-2 font-semibold">Year Built</span>
              </div>
              <p className="text-xl">{safeString(property.year_built, 'N/A')}</p>
            </div>

            <div className="bg-stone-50 p-4 rounded-lg">
              <div className="flex items-center mb-2">
                <HomeIcon />
                <span className="ml-2 font-semibold">Living Area</span>
              </div>
              <p className="text-xl">{safeString(property.total_living_area, 'N/A')} sq ft</p>
            </div>

            <div className="bg-stone-50 p-4 rounded-lg">
              <div className="flex items-center mb-2">
                <InfoIcon />
                <span className="ml-2 font-semibold">Property Type</span>
              </div>
              <p className="text-sm">{safeString(property.property_use_code, 'N/A')}</p>
            </div>

            <div className="bg-stone-50 p-4 rounded-lg">
              <div className="flex items-center mb-2">
                <HomeIcon />
                <span className="ml-2 font-semibold">Building Type</span>
              </div>
              <p className="text-xl">{safeString(property.building_type, 'N/A')}</p>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-4">Property Features</h3>
            <div className="flex flex-wrap gap-3">
              {property.basement === 'Yes' && (
                <span className="bg-gray-100 text-gray-800 px-4 py-2 rounded-lg font-medium">
                  Basement
                </span>
              )}
              {(property.attached_garage === 'Yes' || property.detached_garage === 'Yes') && (
                <span className="bg-blue-100 text-blue-800 px-4 py-2 rounded-lg font-medium">
                  Garage
                  {property.attached_garage === 'Yes' && property.detached_garage === 'Yes' ? ' (Both)' : 
                   property.attached_garage === 'Yes' ? ' (Attached)' : ' (Detached)'}
                </span>
              )}
              {property.pool === 'Yes' && (
                <span className="bg-cyan-100 text-cyan-800 px-4 py-2 rounded-lg font-medium">
                  Swimming Pool
                </span>
              )}
              {property.air_conditioning === 'Yes' && (
                <span className="bg-green-100 text-green-800 px-4 py-2 rounded-lg font-medium">
                  Air Conditioning
                </span>
              )}
              {(property.basement !== 'Yes' && 
                property.attached_garage !== 'Yes' && 
                property.detached_garage !== 'Yes' && 
                property.pool !== 'Yes' && 
                property.air_conditioning !== 'Yes') && (
                <span className="text-stone-500 italic">No special features listed</span>
              )}
            </div>
          </div>

          {isFavorite(property.roll_number) && getNote && saveNote && deleteNote && (
            <PropertyNoteEditor
              propertyId={property.roll_number}
              note={getNote(property.roll_number)}
              onSave={saveNote}
              onDelete={deleteNote}
            />
          )}

          <div>
            <h3 className="text-lg font-semibold mb-4">External Resources</h3>
            <div className="flex flex-wrap gap-4">
              <a 
                href={googleMapsUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center bg-stone-100 hover:bg-stone-200 px-4 py-3 rounded-lg transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                  <circle cx="12" cy="10" r="3"></circle>
                </svg>
                View on Google Maps
              </a>
              
              {honestDoorUrl && (
                <a 
                  href={honestDoorUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center bg-stone-100 hover:bg-stone-200 px-4 py-3 rounded-lg transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                    <path d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"></path>
                  </svg>
                  View on HonestDoor
                </a>
              )}
              
              {property.detail_url && property.detail_url.url && (
                <a 
                  href={property.detail_url.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center bg-stone-100 hover:bg-stone-200 px-4 py-3 rounded-lg transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14,2 14,8 20,8"></polyline>
                    <line x1="16" y1="13" x2="8" y2="13"></line>
                    <line x1="16" y1="17" x2="8" y2="17"></line>
                    <polyline points="10,9 9,9 8,9"></polyline>
                  </svg>
                  City Assessment Details
                </a>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {property.centroid_lat && property.centroid_lon && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <MapIcon />
              <span className="ml-2">Property Location</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <PropertyMap 
              properties={[property]} 
              isFavorite={isFavorite}
              favoritesState={[]}
              onPropertyClick={() => {}}
            />
            <p className="text-xs text-gray-500 mt-3">
              Property location on map. Click marker for additional details.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PropertyDetail;